import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { FinetuneRunService } from "../../../services/FinetuneRunService";
import { DatasetService } from "../../../services/DatasetService";
import { CompositionService } from "../../../services/CompositionService";
import { usePermissions } from "../../../PermissionsProvider";
import moment from "moment";
import { FinetuneRunNew, FinetuneDataset } from "../../../model_gen/finetuning";
import ValidationDetails from "../Validation/ValidationDetails";
import TrainingMetricsChart from "./TrainingMetricsChart";
import FinetuneRunEvaluations from "./FinetuneRunEvaluations";
import {
  handleRetryRun,
  handleDeleteRun,
  handleCancelRun,
  handleRerunFinetune,
} from "./common";
import { Button } from "../../ui/button";
import { Card, CardContent } from "../../ui/card";
import { Input } from "../../ui/input";
import OverflowTabs, { TabItem } from "../../ui/overflow-tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import {
  AppWindowIcon,
  SettingsIcon,
  FlaskConicalIcon,
  BarChart3Icon,
  CalendarIcon,
  ServerIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  Loader2Icon,
  MoreHorizontalIcon,
  RocketIcon,
  RefreshCwIcon,
  CopyIcon,
  TrashIcon,
  TestTubesIcon,
} from "lucide-react";
import { cn } from "../../../lib/utils";
import { notification, Tag } from "antd";

interface TabDefinition {
  key: string;
  label: string;
  icon: React.ReactNode;
  content: React.ReactNode;
}

const FinetuneRunDetail: React.FC = () => {
  const { finetuneRunId } = useParams();
  const { apiKey } = usePermissions();
  const navigate = useNavigate();
  const [finetuneRun, setFinetuneRun] = useState<FinetuneRunNew | null>(null);
  const [dataset, setDataset] = useState<FinetuneDataset | null>(null);
  const [subDataset, setSubDataset] = useState<FinetuneDataset | null>(null);
  const [composition, setComposition] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isDatasetLoading, setIsDatasetLoading] = useState<boolean>(false);
  const [isSubDatasetLoading, setIsSubDatasetLoading] =
    useState<boolean>(false);
  const [isCompositionLoading, setIsCompositionLoading] =
    useState<boolean>(false);
  const [isDeployModalVisible, setIsDeployModalVisible] =
    useState<boolean>(false);
  const [isDeploying, setIsDeploying] = useState<boolean>(false);
  const [modelName, setModelName] = useState<string>("");
  const [modelNameError, setModelNameError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>("overview");
  const [datasets, setDatasets] = useState<{ [key: number]: any }>({});
  const [activeDeployStatuses, setActiveDeployStatuses] = useState<string[]>([]);

  useEffect(() => {
    if (!apiKey) return;
    const sub = FinetuneRunService.getDeployStatuses(null, apiKey).subscribe(
      (data: { all: string[]; active: string[]; terminal: string[] }) => {
        setActiveDeployStatuses(data.active);
      },
      (err: any) => console.error("Failed to fetch deploy statuses:", err)
    );
    return () => sub.unsubscribe();
  }, [apiKey]);

  useEffect(() => {
    if (finetuneRunId) {
      setIsLoading(true);
      FinetuneRunService.getFinetuneRunById(
        Number(finetuneRunId),
        setIsLoading,
        apiKey
      ).subscribe(
        (data: FinetuneRunNew) => {
          setFinetuneRun(data);

          // Fetch composition details if we have a composition ID
          if (data.dataset_composition_id) {
            CompositionService.getComposition(
              data.dataset_composition_id.toString(),
              setIsCompositionLoading,
              apiKey
            ).subscribe(
              (compositionData: any) => {
                setComposition(compositionData);
              },
              (error: any) => {
                console.error("Failed to fetch composition:", error);
                notification.error({
                  message: "Error",
                  description: `Failed to fetch composition details: ${error}`,
                });
              }
            );
          }

          // Fetch dataset details if we have a dataset ID (for legacy support)
          if (data.finetune_dataset_id) {
            DatasetService.getDataset(
              data.finetune_dataset_id,
              setIsDatasetLoading,
              apiKey
            ).subscribe(
              (datasetData: FinetuneDataset) => {
                setDataset(datasetData);
              },
              (error: any) => {
                console.error("Failed to fetch dataset:", error);
              }
            );
          }
          if (data.meta?.sub_dataset_id) {
            DatasetService.getDataset(
              data.meta.sub_dataset_id,
              setIsSubDatasetLoading,
              apiKey
            ).subscribe(
              (datasetData: FinetuneDataset) => {
                setSubDataset(datasetData);
              },
              (error: any) => {
                console.error("Failed to fetch dataset:", error);
              }
            );
          }
        },
        (error: any) => {
          console.error(error);
        }
      );
    }
  }, [finetuneRunId, apiKey]);

  // Fetch datasets when component mounts
  useEffect(() => {
    // Fetch all datasets - we'll use limit 1000 to ensure we get most datasets
    DatasetService.getDatasets(0, 1000, null, apiKey).subscribe(
      (response: { items: any[]; total: number }) => {
        // Create a map of datasets by ID for quick lookup
        const datasetsMap = response.items.reduce((acc, dataset) => {
          acc[dataset.id] = dataset;
          return acc;
        }, {});
        setDatasets(datasetsMap);
      },
      (error: any) => {
        console.error("Failed to load datasets", error);
      }
    );
  }, [apiKey]);

  // Add this helper function to calculate estimated time remaining
  const getTimeRemaining = () => {
    if (
      finetuneRun?.status?.toUpperCase() === "RUNNING" &&
      finetuneRun?.progress &&
      finetuneRun?.date_created
    ) {
      const startTime = moment(finetuneRun.date_created);
      const now = moment();
      const elapsedMinutes = now.diff(startTime, "minutes");

      if (finetuneRun.progress > 0 && elapsedMinutes > 0) {
        // Calculate total time based on progress so far
        const totalEstimatedMinutes = elapsedMinutes / finetuneRun.progress;
        const remainingMinutes = totalEstimatedMinutes - elapsedMinutes;

        if (remainingMinutes <= 0) {
          return "Almost complete";
        }

        if (remainingMinutes < 60) {
          return `${Math.ceil(remainingMinutes)} minutes remaining`;
        } else {
          const hours = Math.floor(remainingMinutes / 60);
          const mins = Math.ceil(remainingMinutes % 60);
          return `${hours}h ${mins}m remaining`;
        }
      }
      return "Calculating...";
    }
    return null;
  };

  const getStatusDisplay = () => {
    if (!finetuneRun) return null;

    const statusUpper = finetuneRun.status.toUpperCase();
    const statusColors = {
      RUNNING: "text-blue-600",
      COMPLETED: "text-green-600",
      FAILED: "text-red-600",
      CANCELLED: "text-orange-600",
      SCHEDULED: "text-yellow-600",
    };

    if (statusUpper === "RUNNING") {
      const percent = finetuneRun.progress
        ? Math.round(finetuneRun.progress * 100)
        : 0;
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Loader2Icon className="w-4 h-4 animate-spin text-blue-600" />
            <span className="text-sm font-gc-med text-blue-600">
              {statusUpper}
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${percent}%` }}
            />
          </div>
          <div className="text-xs text-muted-foreground">
            {percent}% complete • {getTimeRemaining()}
          </div>
        </div>
      );
    } else if (statusUpper === "COMPLETED") {
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <CheckCircleIcon className="w-4 h-4 text-green-600" />
            <span className="text-sm font-gc-med text-green-600">
              {statusUpper}
            </span>
          </div>
          <div className="w-full bg-green-100 dark:bg-green-900/20 rounded-full h-2">
            <div className="bg-green-600 h-2 rounded-full w-full" />
          </div>
        </div>
      );
    } else {
      const IconComponent =
        statusUpper === "FAILED"
          ? XCircleIcon
          : statusUpper === "CANCELLED"
          ? XCircleIcon
          : statusUpper === "SCHEDULED"
          ? ClockIcon
          : ClockIcon;

      return (
        <div className="flex items-center gap-2">
          <IconComponent
            className={cn(
              "w-4 h-4",
              statusColors[statusUpper as keyof typeof statusColors] ||
                "text-muted-foreground"
            )}
          />
          <span
            className={cn(
              "text-sm font-gc-med",
              statusColors[statusUpper as keyof typeof statusColors] ||
                "text-muted-foreground"
            )}>
            {statusUpper}
          </span>
        </div>
      );
    }
  };

  // Helper function to get display name
  const getDisplayName = () => {
    if (!finetuneRun) return "";
    return finetuneRun.display_name;
  };

  // Helper function to render data source
  const renderDataSource = () => {
    if (!finetuneRun) return <span className="text-muted-foreground">-</span>;

    // Helper function to check if a value is valid (not null, undefined, or "null" string)
    const isValidValue = (value: any) => {
      return value && value !== "null" && value !== null && value !== undefined;
    };

    // Check if dataset composition is deleted
    if (finetuneRun.dataset_composition_id === null) {
      return <Tag color="red">DELETED</Tag>;
    }

    if (
      isValidValue(finetuneRun.intent_group_id) &&
      isValidValue(finetuneRun.intent_group_name)
    ) {
      return (
        <button
          onClick={() =>
            navigate(`/intent-group/${finetuneRun.intent_group_id}`)
          }
          className="text-maitai-green hover:underline">
          {finetuneRun.intent_group_name}
        </button>
      );
    }

    if (
      isValidValue(finetuneRun.application_id) &&
      isValidValue(finetuneRun.intent_id)
    ) {
      return (
        <button
          onClick={() =>
            navigate(
              `/application/${finetuneRun.application_id}/intent/${finetuneRun.intent_id}`
            )
          }
          className="text-maitai-green hover:underline">
          {`${finetuneRun.application_name} / ${finetuneRun.intent_name}`}
        </button>
      );
    }

    if (
      isValidValue(finetuneRun.application_name) &&
      isValidValue(finetuneRun.intent_name)
    ) {
      return (
        <span>{`${finetuneRun.application_name} / ${finetuneRun.intent_name}`}</span>
      );
    }

    // Check individual valid values for fallback
    const fallbackText =
      (isValidValue(finetuneRun.application_name)
        ? finetuneRun.application_name
        : null) ||
      (isValidValue(finetuneRun.intent_name)
        ? finetuneRun.intent_name
        : null) ||
      (isValidValue(finetuneRun.intent_group_name)
        ? finetuneRun.intent_group_name
        : null);

    return fallbackText ? (
      <span>{fallbackText}</span>
    ) : (
      <span className="text-muted-foreground">-</span>
    );
  };

  const showDeployModal = () => {
    // Get the display name and format it: replace spaces with underscores and make lowercase
    const displayName = (getDisplayName() as string) || "";
    const initialName = displayName.replace(/\s+/g, "_").toLowerCase();

    setModelName(initialName);
    validateModelName(initialName);
    setIsDeployModalVisible(true);
  };

  const handleDeployCancel = () => {
    if (isDeploying) {
      return; // Prevent closing the modal while deploying
    }
    setIsDeployModalVisible(false);
    setModelName("");
    setModelNameError(null);
  };

  const validateModelName = (name: string): boolean => {
    if (!name.trim()) {
      setModelNameError("Model name cannot be empty");
      return false;
    }

    if (name.length > 50) {
      setModelNameError("Model name cannot exceed 50 characters");
      return false;
    }

    const validNameRegex = /^[a-zA-Z0-9_\-:]+$/;
    if (!validNameRegex.test(name)) {
      setModelNameError(
        "Model name can only contain alphanumeric characters, underscores, hyphens, and colons"
      );
      return false;
    }

    setModelNameError(null);
    return true;
  };

  const handleDeploy = () => {
    if (!validateModelName(modelName)) {
      return;
    }

    setIsDeploying(true);

    FinetuneRunService.deployFinetuneRunVast(
      Number(finetuneRunId),
      modelName,
      setIsDeploying,
      apiKey
    ).subscribe(
      (run: FinetuneRunNew) => {
        console.log(`Model "${modelName}" has been deployed.`);
        setFinetuneRun(run);
        setIsDeployModalVisible(false);
        setModelName("");
        setModelNameError(null);
        setIsDeploying(false);
      },
      (error: any) => {
        console.error(`Failed to deploy model: ${error}`);
        setIsDeploying(false);
      }
    );
  };

  const handleCancelDeploy = () => {
    FinetuneRunService.cancelDeployVast(
      Number(finetuneRunId),
      undefined,
      apiKey
    ).subscribe(
      (run: FinetuneRunNew) => {
        setFinetuneRun(run);
      },
      (error: any) => {
        console.error(`Failed to cancel deploy: ${error}`);
      }
    );
  };

  const isDeployEligible = () => {
    return (
      finetuneRun &&
      (finetuneRun.status === "COMPLETED" || finetuneRun.status === "VALIDATING")
    );
  };

  // Show the initial Deploy button only when no deployment has been attempted
  const shouldShowDeployButton = () => {
    if (!isDeployEligible()) return false;
    const meta = finetuneRun?.meta || {};
    return !meta.model_id && !getDeploymentStatus();
  };

  const shouldShowRetryButton = () => {
    if (!isDeployEligible()) return false;
    const meta = finetuneRun?.meta || {};
    const status = getDeploymentStatus();
    return !meta.model_id && (status === "FAILED" || status === "CANCELLED");
  };

  const isDeployInProgress = () => {
    const status = getDeploymentStatus();
    return status !== null && activeDeployStatuses.includes(status);
  };

  // Get deployment status color
  const getDeploymentStatusColor = (status: string) => {
    const statusMap: Record<string, string> = {
      SCHEDULED:
        "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400",
      PROVISIONING:
        "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400",
      DEPLOYING:
        "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400",
      DEPLOYED:
        "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400",
      FAILED: "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400",
      CANCELLED:
        "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400",
    };
    return statusMap[status] || "bg-muted text-foreground";
  };

  // Helper to safely get deployment status as string
  const getDeploymentStatus = (): string | null => {
    if (!finetuneRun?.meta?.model_deployment_status) {
      return null;
    }
    return String(finetuneRun.meta.model_deployment_status);
  };

  const refreshFinetuneRun = () => {
    if (!finetuneRunId) return;

    setIsLoading(true);
    FinetuneRunService.getFinetuneRunById(
      Number(finetuneRunId),
      setIsLoading,
      apiKey
    ).subscribe(
      (data: FinetuneRunNew) => {
        setFinetuneRun(data);

        // Fetch composition details if we have a composition ID
        if (data.dataset_composition_id) {
          CompositionService.getComposition(
            data.dataset_composition_id.toString(),
            setIsCompositionLoading,
            apiKey
          ).subscribe(
            (compositionData: any) => {
              setComposition(compositionData);
            },
            (error: any) => {
              console.error("Failed to fetch composition:", error);
              notification.error({
                message: "Error",
                description: `Failed to fetch composition details: ${error}`,
              });
            }
          );
        }

        // Fetch dataset details if we have a dataset ID (for legacy support)
        if (data.finetune_dataset_id) {
          DatasetService.getDataset(
            data.finetune_dataset_id,
            setIsDatasetLoading,
            apiKey
          ).subscribe(
            (datasetData: FinetuneDataset) => {
              setDataset(datasetData);
            },
            (error: any) => {
              console.error("Failed to fetch dataset:", error);
            }
          );
        }
        if (data.meta?.sub_dataset_id) {
          DatasetService.getDataset(
            data.meta.sub_dataset_id,
            setIsSubDatasetLoading,
            apiKey
          ).subscribe(
            (datasetData: FinetuneDataset) => {
              setSubDataset(datasetData);
            },
            (error: any) => {
              console.error("Failed to fetch dataset:", error);
            }
          );
        }
      },
      (error: any) => {
        console.error(error);
        setIsLoading(false);
      }
    );
  };

  useEffect(() => {
    const status = getDeploymentStatus();
    const autoTestStatus = finetuneRun?.meta?.auto_test_status;
    const autoTestActive = autoTestStatus && !["COMPLETED", "FAILED"].includes(autoTestStatus);
    const deployActive = status !== null && activeDeployStatuses.includes(status);

    if (!deployActive && !autoTestActive) return;

    const interval = setInterval(() => {
      if (!finetuneRunId) return;
      FinetuneRunService.getFinetuneRunById(
        Number(finetuneRunId),
        undefined,
        apiKey
      ).subscribe(
        (data: FinetuneRunNew) => setFinetuneRun(data),
        (error: any) => console.error("Status poll error:", error)
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [finetuneRun?.meta?.model_deployment_status, finetuneRun?.meta?.auto_test_status, finetuneRunId, apiKey, activeDeployStatuses]);

  const formatDate = (timestamp: number) => {
    if (!timestamp || timestamp <= 0) return "Never";
    const date = moment(timestamp);
    return date.format("MMM D, YYYY");
  };

  const renderOverviewTab = () => (
    <div className="space-y-8">
      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <RocketIcon className="w-4 h-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground font-gc-reg">
                  Base Model
                </p>
              </div>
              <p className="text-lg font-gc-med text-foreground">
                {finetuneRun?.base_model || "Unknown"}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="p-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground font-gc-reg">
                  Date Created
                </p>
              </div>
              <p className="text-lg font-gc-med text-foreground">
                {finetuneRun?.date_created
                  ? formatDate(finetuneRun.date_created)
                  : "-"}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="p-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <ServerIcon className="w-4 h-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground font-gc-reg">
                  Status
                </p>
              </div>
              {getStatusDisplay()}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Details Section */}
      <Card className="border-border">
        <CardContent className="p-6">
          <h3 className="text-lg font-gc-med text-foreground mb-4">Details</h3>
          <div className="space-y-6">
            <div>
              <p className="text-sm text-muted-foreground font-gc-reg mb-1">
                Description
              </p>
              <p className="text-base font-gc-reg text-foreground whitespace-pre-wrap">
                {finetuneRun?.description || "No description provided"}
              </p>
            </div>

            <div>
              <p className="text-sm text-muted-foreground font-gc-reg mb-1">
                {finetuneRun?.dataset_composition_id
                  ? "Composition"
                  : "Dataset"}
              </p>
              <div>
                {finetuneRun?.dataset_composition_id ? (
                  // Show composition info
                  isCompositionLoading ? (
                    <div className="text-sm text-muted-foreground">
                      Loading...
                    </div>
                  ) : composition ? (
                    <Link
                      to={`/forge/compositions/${finetuneRun.dataset_composition_id}`}
                      className="text-maitai-green hover:underline text-sm">
                      {composition.name}
                    </Link>
                  ) : (
                    <Link
                      to={`/forge/compositions/${finetuneRun.dataset_composition_id}`}
                      className="text-maitai-green hover:underline text-sm">
                      Composition
                    </Link>
                  )
                ) : // Show dataset info (legacy support)
                isDatasetLoading ? (
                  <div className="text-sm text-muted-foreground">
                    Loading...
                  </div>
                ) : dataset ? (
                  <Link
                    to={`/forge/datasets/${finetuneRun?.finetune_dataset_id}`}
                    className="text-maitai-green hover:underline text-sm">
                    {dataset.name}
                  </Link>
                ) : finetuneRun?.finetune_dataset_id ? (
                  <Link
                    to={`/forge/datasets/${finetuneRun.finetune_dataset_id}`}
                    className="text-maitai-green hover:underline text-sm">
                    Dataset
                  </Link>
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </div>
              {finetuneRun?.meta?.sub_dataset_id && (
                <div className="mt-2">
                  <p className="text-sm text-muted-foreground font-gc-reg mb-1">
                    Sub Dataset
                  </p>
                  <div>
                    {isSubDatasetLoading ? (
                      <div className="text-sm text-muted-foreground">
                        Loading...
                      </div>
                    ) : subDataset ? (
                      <Link
                        to={`/forge/datasets/${finetuneRun?.meta?.sub_dataset_id}`}
                        className="text-maitai-green hover:underline text-sm">
                        {subDataset.name}
                      </Link>
                    ) : finetuneRun?.meta?.sub_dataset_id ? (
                      <Link
                        to={`/forge/datasets/${finetuneRun.meta.sub_dataset_id}`}
                        className="text-maitai-green hover:underline text-sm">
                        Dataset
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div>
              <p className="text-sm text-muted-foreground font-gc-reg mb-1">
                Data Source
              </p>
              <div className="text-base font-gc-reg text-foreground">
                {renderDataSource()}
              </div>
            </div>

          </div>
        </CardContent>
      </Card>

      {/* Prompt Section - if available */}
      {finetuneRun?.prompt && (
        <Card className="border-border">
          <CardContent className="p-6">
            <h3 className="text-lg font-gc-med text-foreground mb-4">Prompt</h3>
            <pre className="text-sm font-mono bg-muted p-4 rounded text-foreground whitespace-pre-wrap overflow-x-auto">
              {finetuneRun.prompt}
            </pre>
          </CardContent>
        </Card>
      )}

      {/* Prompt Deletions - if available */}
      {finetuneRun?.prompt_deletions &&
        finetuneRun.prompt_deletions.length > 0 && (
          <Card className="border-border">
            <CardContent className="p-6">
              <h3 className="text-lg font-gc-med text-foreground mb-4">
                Prompt Deletions
              </h3>
              <ul className="space-y-2">
                {finetuneRun.prompt_deletions.map((deletion, index) => (
                  <li
                    key={index}
                    className="text-sm font-gc-reg text-foreground flex items-start gap-2">
                    <span className="text-muted-foreground">•</span>
                    {deletion}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
    </div>
  );

  const renderConfigurationTab = () => (
    <div className="space-y-8">
      <Card className="border-border">
        <CardContent className="p-6">
          <h3 className="text-lg font-gc-med text-foreground mb-4">
            Configuration Settings
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-muted-foreground font-gc-reg mb-1">
                Conversation Mode
              </p>
              <p className="text-base font-gc-reg text-foreground">
                {finetuneRun?.conversation_mode ? "Yes" : "No"}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground font-gc-reg mb-1">
                4-bit Quantization
              </p>
              <p className="text-base font-gc-reg text-foreground">
                {finetuneRun?.fourbit ? "Yes" : "No"}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground font-gc-reg mb-1">
                Remove Examples
              </p>
              <p className="text-base font-gc-reg text-foreground">
                {finetuneRun?.remove_examples ? "Yes" : "No"}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground font-gc-reg mb-1">
                Run Version
              </p>
              <p className="text-base font-gc-reg text-foreground">
                {finetuneRun?.run_version || "-"}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground font-gc-reg mb-1">
                Remove Example Tag
              </p>
              <p className="text-base font-gc-reg text-foreground">
                {finetuneRun?.remove_example_tag ? "Yes" : "No"}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground font-gc-reg mb-1">
                Remove Output Tag
              </p>
              <p className="text-base font-gc-reg text-foreground">
                {finetuneRun?.remove_output_tag ? "Yes" : "No"}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground font-gc-reg mb-1">
                Warmup Ratio
              </p>
              <p className="text-base font-gc-reg text-foreground">
                {finetuneRun?.meta?.warmup_ratio !== undefined
                  ? String(finetuneRun.meta.warmup_ratio)
                  : "-"}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground font-gc-reg mb-1">
                Learning Rate
              </p>
              <p className="text-base font-gc-reg text-foreground">
                {finetuneRun?.meta?.learning_rate !== undefined
                  ? String(finetuneRun.meta.learning_rate)
                  : "-"}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground font-gc-reg mb-1">
                Per Device Train Batch Size
              </p>
              <p className="text-base font-gc-reg text-foreground">
                {finetuneRun?.meta?.per_device_train_batch_size !== undefined
                  ? String(finetuneRun.meta.per_device_train_batch_size)
                  : "-"}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground font-gc-reg mb-1">
                Gradient Accumulation Steps
              </p>
              <p className="text-base font-gc-reg text-foreground">
                {finetuneRun?.meta?.gradient_accumulation_steps !== undefined
                  ? String(finetuneRun.meta.gradient_accumulation_steps)
                  : "-"}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground font-gc-reg mb-1">
                Train Split
              </p>
              <p className="text-base font-gc-reg text-foreground">
                {finetuneRun?.meta && "train_split" in finetuneRun.meta
                  ? `${((finetuneRun.meta as any).train_split * 100).toFixed(
                      0
                    )}%`
                  : "-"}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground font-gc-reg mb-1">
                Epochs
              </p>
              <p className="text-base font-gc-reg text-foreground">
                {finetuneRun?.meta && "num_train_epochs" in finetuneRun.meta
                  ? String(finetuneRun?.meta?.num_train_epochs)
                  : "-"}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground font-gc-reg mb-1">
                Weight Decay
              </p>
              <p className="text-base font-gc-reg text-foreground">
                {finetuneRun?.meta && "weight_decay" in finetuneRun.meta
                  ? String((finetuneRun.meta as any).weight_decay)
                  : "-"}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground font-gc-reg mb-1">
                Dropout
              </p>
              <p className="text-base font-gc-reg text-foreground">
                {finetuneRun?.meta && "dropout" in finetuneRun.meta
                  ? String((finetuneRun.meta as any).dropout)
                  : "-"}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground font-gc-reg mb-1">
                Rank
              </p>
              <p className="text-base font-gc-reg text-foreground">
                {finetuneRun?.meta && "rank" in finetuneRun.meta
                  ? String((finetuneRun.meta as any).rank)
                  : "-"}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground font-gc-reg mb-1">
                Alpha
              </p>
              <p className="text-base font-gc-reg text-foreground">
                {finetuneRun?.meta && "alpha" in finetuneRun.meta
                  ? String((finetuneRun.meta as any).alpha)
                  : "-"}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground font-gc-reg mb-1">
                RSLoRA
              </p>
              <p className="text-base font-gc-reg text-foreground">
                {finetuneRun?.meta?.rslora ? "Yes" : "No"}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground font-gc-reg mb-1">
                Train on Responses Only
              </p>
              <p className="text-base font-gc-reg text-foreground">
                {finetuneRun?.meta?.train_on_responses_only ? "Yes" : "No"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderValidationTab = () => (
    <ValidationDetails finetuneRunId={finetuneRun?.id.toString()} />
  );

  // Tab configuration
  const tabDefinitions: TabDefinition[] = [
    {
      key: "overview",
      label: "Overview",
      icon: <AppWindowIcon className="w-4 h-4" />,
      content: renderOverviewTab(),
    },
    {
      key: "configuration",
      label: "Configuration",
      icon: <SettingsIcon className="w-4 h-4" />,
      content: renderConfigurationTab(),
    },
    {
      key: "training",
      label: "Training",
      icon: <BarChart3Icon className="w-4 h-4" />,
      content: finetuneRun ? (
        <TrainingMetricsChart finetuneRunId={finetuneRun.id} />
      ) : null,
    },
    {
      key: "validation",
      label: "Eval Results",
      icon: <FlaskConicalIcon className="w-4 h-4" />,
      content: finetuneRun ? renderValidationTab() : null,
    },
    {
      key: "evaluations",
      label: "Test Set Runs",
      icon: <TestTubesIcon className="w-4 h-4" />,
      content: finetuneRun ? (
        <FinetuneRunEvaluations
          finetuneRun={finetuneRun}
          apiKey={apiKey}
          onFinetuneRunUpdate={setFinetuneRun}
        />
      ) : null,
    },
  ];

  // Create tabs array for OverflowTabs component
  const tabs: TabItem[] = tabDefinitions.map((tab) => ({
    key: tab.key,
    label: tab.label,
    icon: tab.icon,
  }));

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
          {/* Header Card Loading */}
          <Card className="border-border">
            <CardContent className="p-8">
              <div className="h-4 bg-muted rounded w-32 animate-pulse mb-6" />
              <div className="space-y-2">
                <div className="h-10 bg-muted rounded w-64 animate-pulse" />
                <div className="h-5 bg-muted rounded w-96 animate-pulse" />
              </div>
            </CardContent>
          </Card>

          {/* Content Loading */}
          <Card className="border-border">
            <CardContent className="p-8">
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="h-4 bg-muted rounded flex-1 animate-pulse" />
                    <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                    <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                    <div className="h-4 bg-muted rounded w-8 animate-pulse" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {/* Breadcrumb Navigation */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button
                    onClick={() => navigate("/forge/finetuning")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg">
                    Finetune Runs
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <span className="text-maitai-green font-gc-med">
                    {getDisplayName()}
                  </span>
                </li>
              </ol>
            </nav>

            {/* Page Header */}
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                  {getDisplayName()}
                </h1>
                <p className="text-lg text-muted-foreground font-gc-reg">
                  Monitor fine-tuning progress, validate results, and deploy
                  models
                </p>
              </div>
              <div className="flex items-center gap-3">
                {finetuneRun && (
                  <>
                    {shouldShowDeployButton() && (
                      <Button onClick={showDeployModal} className="gap-2">
                        <RocketIcon className="w-4 h-4" />
                        Deploy
                      </Button>
                    )}
                    {isDeployInProgress() && (
                      <div className="flex items-center gap-2">
                        <span
                          className={cn(
                            "inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium",
                            getDeploymentStatusColor(getDeploymentStatus()!)
                          )}>
                          <Loader2Icon className="w-3 h-3 animate-spin" />
                          {getDeploymentStatus()}
                        </span>
                        <Button
                          onClick={handleCancelDeploy}
                          variant="ghost"
                          size="sm"
                          className="gap-1.5 text-muted-foreground hover:text-destructive">
                          <XCircleIcon className="w-3 h-3" />
                          Cancel
                        </Button>
                      </div>
                    )}
                    {getDeploymentStatus() === "DEPLOYED" && (
                      <span
                        className={cn(
                          "inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium",
                          getDeploymentStatusColor("DEPLOYED")
                        )}>
                        <CheckCircleIcon className="w-3 h-3" />
                        DEPLOYED
                      </span>
                    )}
                    {shouldShowRetryButton() && (
                      <div className="flex items-center gap-2">
                        <span
                          className={cn(
                            "inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium",
                            getDeploymentStatusColor(getDeploymentStatus()!)
                          )}>
                          <XCircleIcon className="w-3 h-3" />
                          {getDeploymentStatus()}
                        </span>
                        <Button
                          onClick={showDeployModal}
                          variant="outline"
                          size="sm"
                          className="gap-1.5">
                          <RefreshCwIcon className="w-3 h-3" />
                          Retry
                        </Button>
                      </div>
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="gap-2">
                          <MoreHorizontalIcon className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem
                          onClick={() =>
                            handleRerunFinetune(finetuneRun, datasets, navigate)
                          }>
                          <CopyIcon className="w-4 h-4" />
                          Copy
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          disabled={
                            !["FAILED", "COMPLETED", "SCHEDULED", "CANCELLED"].includes(
                              finetuneRun.status.toUpperCase()
                            )
                          }
                          onClick={() =>
                            handleRetryRun(
                              finetuneRun,
                              setIsLoading,
                              apiKey,
                              refreshFinetuneRun
                            )
                          }>
                          <RefreshCwIcon className="w-4 h-4" />
                          Retry
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-red-600 focus:text-red-600"
                          onClick={() => {
                            if (
                              ["RUNNING", "SCHEDULED", "VALIDATING"].includes(
                                finetuneRun.status.toUpperCase()
                              )
                            ) {
                              handleCancelRun(
                                finetuneRun,
                                setIsLoading,
                                apiKey,
                                refreshFinetuneRun
                              );
                            } else {
                              handleDeleteRun(
                                finetuneRun,
                                setIsLoading,
                                apiKey,
                                () => navigate("/forge/finetuning")
                              );
                            }
                          }}>
                          <TrashIcon className="w-4 h-4" />
                          {["RUNNING", "SCHEDULED", "VALIDATING"].includes(
                            finetuneRun.status.toUpperCase()
                          )
                            ? "Cancel"
                            : "Delete"}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Card className="border-border">
          <CardContent className="p-0">
            {/* Tab Navigation */}
            <OverflowTabs
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              className="border-b border-border px-8 py-4"
            />

            {/* Tab Content */}
            <div className="p-8">
              {tabDefinitions.find((tab) => tab.key === activeTab)?.content}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Deploy Model Modal */}
      {isDeployModalVisible && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <RocketIcon className="w-5 h-5 text-maitai-green" />
                  <h3 className="text-lg font-gc-med text-foreground">
                    Deploy Finetuned Model
                  </h3>
                </div>

                {isDeploying ? (
                  <div className="text-center py-8">
                    <Loader2Icon className="w-8 h-8 animate-spin mx-auto mb-4 text-maitai-green" />
                    <p className="text-sm text-muted-foreground">
                      Deploying model...
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground">
                      Please provide a name for your deployed model:
                    </p>
                    <Input
                      placeholder="Model name"
                      value={modelName}
                      onChange={(e) => {
                        const value = e.target.value;
                        setModelName(value);
                        validateModelName(value);
                      }}
                      maxLength={50}
                      className={modelNameError ? "border-red-500" : ""}
                    />
                    {modelNameError && (
                      <p className="text-sm text-red-600">{modelNameError}</p>
                    )}
                    <div className="flex items-center gap-3 pt-4">
                      <Button
                        variant="outline"
                        onClick={handleDeployCancel}
                        disabled={isDeploying}
                        className="flex-1">
                        Cancel
                      </Button>
                      <Button
                        onClick={handleDeploy}
                        disabled={
                          !modelName.trim() ||
                          modelNameError !== null ||
                          isDeploying
                        }
                        className="flex-1">
                        Deploy
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default FinetuneRunDetail;
